import { clsx, type ClassValue } from 'clsx'
import { twMerge } from 'tailwind-merge'

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs))
}

export function formatDate(date: string | Date, options?: Intl.DateTimeFormatOptions) {
  return new Intl.DateTimeFormat('en-US', {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
    ...options,
  }).format(new Date(date))
}

function resolveCurrencyFromPersistedAuth() {
  if (typeof window === 'undefined') {
    return { symbol: '', code: '' }
  }

  try {
    const raw = window.localStorage.getItem('auth-storage')
    if (!raw) {
      return { symbol: '', code: '' }
    }

    const parsed = JSON.parse(raw) as {
      state?: {
        serviceInstanceId?: string | number | null
        currentBranch?: {
          id?: string | number | null
          instanceId?: string | number | null
          code?: string | number | null
          currencySymbol?: string | null
          currencyCode?: string | null
        } | null
        availableBranches?: Array<{
          id?: string | number | null
          instanceId?: string | number | null
          code?: string | number | null
          currencySymbol?: string | null
          currencyCode?: string | null
        }> | null
        activeCurrency?: {
          symbol?: string | null
          code?: string | null
        } | null
      }
    }

    const state = parsed?.state

    // Currency configured for whichever service instance (org/tenant/branch)
    // is currently active. Org/tenant-scoped posts intentionally leave
    // `currentBranch` null (no single "current branch" to switch to), which
    // otherwise left this resolver with nothing to read and defaulting to
    // USD/$ even though the tenant has its own currency configured. Prefer
    // this whenever it is populated.
    const activeCurrency = state?.activeCurrency
    if (activeCurrency && (activeCurrency.symbol || activeCurrency.code)) {
      return {
        symbol: String(activeCurrency.symbol ?? '').trim(),
        code: String(activeCurrency.code ?? '').trim(),
      }
    }

    const serviceInstanceId = String(state?.serviceInstanceId ?? '').trim()
    const current = state?.currentBranch

    if (current) {
      const currentMatches = [current.instanceId, current.id, current.code]
        .map((value) => String(value ?? '').trim())
        .some((value) => value && value === serviceInstanceId)

      if (!serviceInstanceId || currentMatches) {
        return {
          symbol: String(current.currencySymbol ?? '').trim(),
          code: String(current.currencyCode ?? '').trim(),
        }
      }
    }

    const matched = (state?.availableBranches ?? []).find((branch) => {
      const candidates = [branch.instanceId, branch.id, branch.code]
        .map((value) => String(value ?? '').trim())
      return serviceInstanceId ? candidates.some((value) => value && value === serviceInstanceId) : false
    })

    return {
      symbol: String(matched?.currencySymbol ?? current?.currencySymbol ?? '').trim(),
      code: String(matched?.currencyCode ?? current?.currencyCode ?? '').trim(),
    }
  } catch {
    return { symbol: '', code: '' }
  }
}

export function getCurrencySymbol(currency?: string) {
  const activeCurrency = resolveCurrencyFromPersistedAuth()
  if (!currency && activeCurrency.symbol) {
    return activeCurrency.symbol
  }

  const resolvedCode = String((currency ?? activeCurrency.code) || 'USD').trim().toUpperCase()
  const parts = new Intl.NumberFormat('en-US', { style: 'currency', currency: resolvedCode }).formatToParts(0)
  return parts.find((part) => part.type === 'currency')?.value ?? ''
}

export function formatCurrency(amount: number, currency?: string) {
  const numericAmount = Number.isFinite(amount) ? amount : 0
  const activeCurrency = resolveCurrencyFromPersistedAuth()
  const resolvedCode = String((currency ?? activeCurrency.code) || 'USD').trim().toUpperCase()

  if (!currency && activeCurrency.symbol) {
    const sign = numericAmount < 0 ? '-' : ''
    const formatted = Math.abs(numericAmount).toLocaleString(undefined, {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    })
    return `${sign}${activeCurrency.symbol}${formatted}`
  }

  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: resolvedCode,
  }).format(numericAmount)
}

export function formatWholeCurrency(amount: number, currencySymbol?: string | null) {
  const numericAmount = Number.isFinite(amount) ? Math.round(amount) : 0
  const activeCurrency = resolveCurrencyFromPersistedAuth()
  const symbol = currencySymbol ?? activeCurrency.symbol ?? ''
  const sign = numericAmount < 0 ? '-' : ''
  const formatted = Math.abs(numericAmount).toLocaleString(undefined, { maximumFractionDigits: 0 })
  return symbol ? `${sign}${symbol}${formatted}` : `${sign}${formatted}`
}

export function formatNumber(num: number) {
  return new Intl.NumberFormat('en-US').format(num)
}

export function capitalize(str: string) {
  return str.charAt(0).toUpperCase() + str.slice(1).toLowerCase()
}

export function slugify(str: string) {
  return str
    .toLowerCase()
    .replace(/[^\w\s-]/g, '')
    .replace(/[\s_-]+/g, '-')
    .replace(/^-+|-+$/g, '')
}

export function truncate(str: string, length: number) {
  if (str.length <= length) return str
  return str.slice(0, length) + '...'
}

export function getInitials(name: string) {
  return name
    .split(' ')
    .map((n) => n[0])
    .join('')
    .toUpperCase()
    .slice(0, 2)
}

export function debounce<T extends (...args: unknown[]) => unknown>(
  func: T,
  wait: number
): (...args: Parameters<T>) => void {
  let timeout: ReturnType<typeof setTimeout> | null = null

  return function executedFunction(...args: Parameters<T>) {
    const later = () => {
      timeout = null
      func(...args)
    }

    if (timeout) {
      clearTimeout(timeout)
    }
    timeout = setTimeout(later, wait)
  }
}
